// The slice map: every chip where the topology puts it, each with a strip per core
// over the whole run plus an "in" and an "out" strip for the DMAs that crossed to or
// from another chip (coloured by the peer), and beside the grid a source-by-
// destination matrix of that traffic inside the brushed window. Strips reuse the
// timeline's bins, so what a strip shows and what the collapsed chip row shows are
// the same numbers.

import { el, fmtPs, bytes, showTip, hideTip, clamp, esc } from "./dom.js";
import { unitRGB, unitColor, DOMINANT, UNIT_ABOUT, UNIT_BIT } from "./isa.js";
import { levelFor, sampleColumn, DOM_NONE } from "./bins.js";
import { issuer } from "./fmt.js";

const TC_H = 12, SC_H = 6, SC_STREAM_H = 12, ICI_H = 6, HOST_H = 5, GAP = 2;

// One colour per chip, for "which peer": distinct hues that read in both themes.
const PEER = ["#e6553f", "#3f8ee6", "#3fbf6e", "#d9a42a", "#a05fd6", "#2bb5c4", "#e07ab3", "#8a9a2a", "#f08a3a", "#5a6fd6", "#3fa38a", "#c04f6a"];
export const peerColor = (chip) => PEER[chip % PEER.length];

// Profiler-style handling of the window drawn over a strip, shared by the minimap and
// every card: drag outside the window to select a new one, drag its edges to resize
// it, drag inside to move it, wheel to zoom it about the pointer (shift, or a sideways
// wheel, pans). A press that does not move is a click, which the caller handles.
function bindWindow(elem, api) {
  const EDGE = 6;
  let drag = null;
  const span = () => api.span();
  const px2t = (x) => { const [a, b] = span(); return a + clamp(x / api.width(), 0, 1) * (b - a); };
  const t2px = (t) => { const [a, b] = span(); return (t - a) / (b - a) * api.width(); };
  const localX = (e) => e.clientX - elem.getBoundingClientRect().left;
  const zone = (x) => {
    const [b0, b1] = api.get(), [S0, S1] = span(), x0 = t2px(b0), x1 = t2px(b1);
    if (b0 <= S0 && b1 >= S1) return "new";     // nothing to move or resize yet
    if (Math.abs(x - x0) <= EDGE) return "lo";
    if (Math.abs(x - x1) <= EDGE) return "hi";
    return x > x0 && x < x1 ? "move" : "new";
  };
  const CURSOR = { lo: "col-resize", hi: "col-resize", move: "grab", new: "crosshair" };
  elem.addEventListener("pointerdown", (e) => {
    if (e.button !== 0) return;
    const x = localX(e), [b0, b1] = api.get();
    drag = { kind: zone(x), x, t: px2t(x), b0, b1, moved: false };
    try { elem.setPointerCapture(e.pointerId); } catch (_) { /* synthetic events */ }
    e.preventDefault();
  });
  elem.addEventListener("pointermove", (e) => {
    const x = localX(e);
    if (!drag) { elem.style.cursor = CURSOR[zone(x)]; if (api.move) api.move(e); return; }
    if (!drag.moved) { if (Math.abs(x - drag.x) <= 3) return; drag.moved = true; if (api.leave) api.leave(e); }
    elem.style.cursor = drag.kind === "move" ? "grabbing" : CURSOR[drag.kind];
    const [S0, S1] = span(), t = px2t(x);
    let b0 = drag.b0, b1 = drag.b1;
    if (drag.kind === "lo") b0 = Math.min(t, b1 - 1);
    else if (drag.kind === "hi") b1 = Math.max(t, b0 + 1);
    else if (drag.kind === "move") { const w = drag.b1 - drag.b0; b0 = clamp(drag.b0 + (t - drag.t), S0, S1 - w); b1 = b0 + w; }
    else { b0 = Math.min(drag.t, t); b1 = Math.max(drag.t, t); if (b1 - b0 < 1) b1 = b0 + 1; }
    api.set(b0, b1);
  });
  elem.addEventListener("pointerup", (e) => {
    if (!drag) return;
    const d = drag;
    drag = null;
    elem.style.cursor = CURSOR[zone(localX(e))];
    if (!d.moved && api.click) api.click(e);
  });
  elem.addEventListener("pointercancel", () => { drag = null; });
  elem.addEventListener("pointerleave", (e) => { if (!drag && api.leave) api.leave(e); });
  elem.addEventListener("wheel", (e) => {
    e.preventDefault();
    const [S0, S1] = span();
    let [b0, b1] = api.get();
    const w = b1 - b0;
    if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      b0 = clamp(b0 + (e.deltaX || e.deltaY) / api.width() * w, S0, S1 - w); b1 = b0 + w;
    } else {
      const t = px2t(localX(e)), nw = clamp(w * Math.exp(e.deltaY * 0.002), 1, S1 - S0);
      b0 = clamp(t - (t - b0) * (nw / w), S0, S1 - nw); b1 = b0 + nw;
    }
    api.set(b0, b1);
  }, { passive: false });
}

export class SliceMap {
  constructor(root, ctx, opts = {}) {
    this.ctx = ctx;
    this.model = ctx.model;
    this.links = ctx.links;
    this.bins = ctx.bins;
    this.store = ctx.store;
    this.opts = { stripPx: 320, showBrush: true, ...opts };
    this.root = root;
    this.brush = this.model.span.slice();
    this.cards = new Map();
    this.hl = null;
    this.tmp = {};
    this.build();
  }

  build() {
    const m = this.model;
    this.root.classList.add("sm");
    this.root.textContent = "";
    this.planes = el("div", "sm-planes");
    this.root.appendChild(this.planes);
    const [X, Y, Z] = m.shape;
    this.grids = [];
    for (let z = 0; z < Z; z++) {
      const wrap = el("div", "sm-plane");
      if (Z > 1) wrap.appendChild(el("div", "sm-plane-title dim", `z = ${z}`));
      const grid = el("div", "sm-grid");
      grid.style.gridTemplateColumns = `repeat(${X}, max-content)`;
      wrap.appendChild(grid);
      this.planes.appendChild(wrap);
      this.grids.push({ grid, wrap });
    }
    const unplaced = [];
    for (const chip of m.chipIds) {
      const [x, y, z] = m.coords(chip);
      const card = this.card(chip);
      if (z < Z && x < X && y < Y) {
        card.style.gridColumn = String(x + 1);
        card.style.gridRow = String(y + 1);
        this.grids[z].grid.appendChild(card);
      } else unplaced.push(card);
    }
    if (unplaced.length) {
      const wrap = el("div", "sm-plane");
      wrap.appendChild(el("div", "sm-plane-title dim", "chips outside the slice shape"));
      const grid = el("div", "sm-grid");
      grid.style.gridTemplateColumns = `repeat(${unplaced.length}, max-content)`;
      for (const c of unplaced) grid.appendChild(c);
      wrap.appendChild(grid);
      this.planes.appendChild(wrap);
    }
    this.matrix = el("div", "sm-matrix");
    this.planes.appendChild(this.matrix);
    this.legend = el("div", "sm-legend legend");
    this.root.appendChild(this.legend);
    if (this.opts.showBrush) this.buildBrush();
    this.render();
  }

  destroy() { hideTip(); }

  // One chip: a title and a canvas of strips.
  card(chip) {
    const m = this.model;
    const cores = m.coresOfChip(chip);
    const scs = m.scsOfChip(chip);
    const card = el("div", "sm-card");
    card.dataset.chip = chip;
    const title = el("div", "sm-title");
    const sw = el("span", "swatch"); sw.style.background = peerColor(chip);
    title.appendChild(sw);
    title.appendChild(document.createTextNode(m.chipLabel(chip)));
    title.onclick = () => { if (this.opts.onChipClick) this.opts.onChipClick(chip); };
    title.ondblclick = () => { if (this.opts.onChipDblClick) this.opts.onChipDblClick(chip); };
    if (this.opts.onChipDblClick) title.title = "double-click to open this chip";
    card.appendChild(title);
    const rows = [];
    for (let ci = 0; ci < m.tcPerChip; ci++) {
      const core = cores.find((c) => c.ci === ci) || null;
      rows.push({ kind: "tc", ci, core, id: core ? core.key : `c${chip}t${ci}`, h: TC_H, label: "TC" + ci });
    }
    const nsc = Math.max(m.scPerChip, scs.length ? Math.max(...scs) + 1 : 0);
    for (let ci = 0; ci < nsc; ci++) {
      const core = m.scCoresOfChip(chip).find((c) => c.ci === ci) || null;
      rows.push({ kind: "sc", ci, core, id: `c${chip}s${ci}`, h: core ? SC_STREAM_H : SC_H, label: "SC" + ci, seen: scs.includes(ci), tiles: m.tilesOf(chip, ci).length });
    }
    if (m.chipIds.length > 1) {
      rows.push({ kind: "in", id: `c${chip}in`, h: ICI_H, label: "in" });
      rows.push({ kind: "out", id: `c${chip}out`, h: ICI_H, label: "out" });
    }
    rows.push({ kind: "host", id: `c${chip}host`, h: HOST_H, label: "host" });
    let y = 0;
    for (const r of rows) { r.y = y; y += r.h + GAP; }
    const labels = el("div", "sm-labels");
    for (const r of rows) {
      const l = el("div", "sm-label " + r.kind, r.label);
      l.style.top = r.y + "px"; l.style.height = r.h + "px";
      labels.appendChild(l);
    }
    const body = el("div", "sm-body");
    const cv = el("canvas");
    cv.width = this.opts.stripPx * (window.devicePixelRatio || 1);
    cv.height = y * (window.devicePixelRatio || 1);
    cv.style.width = this.opts.stripPx + "px"; cv.style.height = y + "px";
    body.appendChild(labels);
    body.appendChild(cv);
    card.appendChild(body);
    const rec = { chip, card, cv, rows, height: y };
    this.cards.set(chip, rec);
    const rowAt = (e) => rows.find((x) => e.offsetY >= x.y && e.offsetY < x.y + x.h);
    bindWindow(cv, {
      width: () => this.opts.stripPx, span: () => this.span, get: () => this.brush,
      set: (a, b) => this.setBrush(a, b, true),
      move: (e) => {
        const r = rowAt(e);
        if (!r) { hideTip(); return; }
        showTip(e.clientX, e.clientY, this.rowTip(chip, r, this.tAt(e.offsetX)));
        this.store.set({ hlRow: r.id });
      },
      leave: () => { hideTip(); this.store.set({ hlRow: null }); },
      click: (e) => { const r = rowAt(e); if (r && this.opts.onCoreClick) this.opts.onCoreClick(chip, r, this.tAt(e.offsetX)); },
    });
    cv.ondblclick = (e) => {
      const r = rows.find((x) => e.offsetY >= x.y && e.offsetY < x.y + x.h);
      if (r && this.opts.onCoreDblClick) this.opts.onCoreDblClick(chip, r, this.tAt(e.offsetX));
    };
    return card;
  }

  // The strips' axis: the cores' active span.
  get span() { return this.model.span; }
  tAt(x) { const [a, b] = this.span; return a + (x / this.opts.stripPx) * (b - a); }
  xOf(t) { const [a, b] = this.span; return (t - a) / (b - a) * this.opts.stripPx; }

  rowTip(chip, r, t) {
    const m = this.model;
    if (r.kind === "sc" && r.core) {
      const c = r.core;
      const lvl = levelFor(this.bins.core(c), (this.span[1] - this.span[0]) / this.opts.stripPx);
      const o = sampleColumn(lvl, t, t + (this.span[1] - this.span[0]) / this.opts.stripPx, this.tmp);
      const waits = Array.from(this.links.waitsByRow.get(c.key) || [], (i) => m.waits[i]);
      let tiles = "";
      if (r.tiles) {
        const tl = levelFor(this.bins.tiles(chip, r.ci), (this.span[1] - this.span[0]) / this.opts.stripPx);
        const to = sampleColumn(tl, t, t + (this.span[1] - this.span[0]) / this.opts.stripPx, {});
        tiles = `\ntiles: ${to.active} of ${r.tiles} active here, issuing ${(to.busy * 100).toFixed(0)}% of the time`;
      }
      return `<b>${esc(c.label)}</b>  ${c.n.toLocaleString()} bundles${r.tiles ? ", " + r.tiles + " tile streams" : ""}\nat ${fmtPs(t)}: issuing ${(o.busy * 100).toFixed(0)}%, waiting ${(o.wait * 100).toFixed(0)}%${o.dom !== DOM_NONE ? ", mostly " + DOMINANT[o.dom] : ""}${tiles}\n${waits.length} waits, ${fmtPs(waits.reduce((a, w) => a + w.stallPs, 0))} stalled`;
    }
    if (r.kind === "tc") {
      if (!r.core) return `<b>chip ${chip} TC${r.ci}</b>\nno dispatch on this core`;
      const c = r.core;
      const pp = (this.span[1] - this.span[0]) / this.opts.stripPx;
      const lvl = levelFor(this.bins.core(c), pp);
      const o = sampleColumn(lvl, t - pp / 2, t + pp / 2, this.tmp);
      const waits = Array.from(this.links.waitsByRow.get(c.key) || [], (i) => m.waits[i]);
      const stall = waits.reduce((a, w) => a + w.stallPs, 0);
      const longest = waits.reduce((a, w) => Math.max(a, w.stallPs), 0);
      return `<b>${esc(c.label)}</b>  ${c.n.toLocaleString()} bundles, ${fmtPs(c.tLast)} of work\nat ${fmtPs(t)}: issuing ${(o.busy * 100).toFixed(0)}%, waiting ${(o.wait * 100).toFixed(0)}%, padding ${(o.nop * 100).toFixed(0)}%${o.dom !== DOM_NONE ? ", mostly " + DOMINANT[o.dom] : ""}\n${waits.length} waits, ${fmtPs(stall)} stalled, longest ${fmtPs(longest)}`;
    }
    if (r.kind === "sc") {
      const waits = Array.from(this.links.waitsByRow.get(r.id) || [], (i) => m.waits[i]);
      const dmas = (this.links.dmaByIssuer.get(r.id) || []).length;
      const disp = m.scDispatches.filter((d) => d.chip === chip && d.ci === r.ci).length;
      return `<b>chip ${chip} SC${r.ci}</b>${r.seen ? "" : "  (no events)"}\n${disp} dispatches, ${waits.length} waits (${fmtPs(waits.reduce((a, w) => a + w.stallPs, 0))} stalled), ${dmas} DMAs issued\nSparseCores leave no bundle trace; only their flags, DMAs and dispatches are visible`;
    }
    if (r.kind === "in" || r.kind === "out") {
      const list = this.iciDmas(chip, r.kind);
      const here = list.filter(({ d }) => { const a = Number.isNaN(d.at) ? d.start : d.at; return a <= t && (d.open ? m.tEnd : d.end) >= t; });
      const total = list.reduce((a, x) => a + (x.d.bytes || 0), 0);
      const peers = [...new Set(list.map((x) => x.peer))].sort((a, b) => a - b);
      const now = here.length ? `\nat ${fmtPs(t)}: ${here.map(({ d, peer }) => `DMA #${d.id} ${bytes(d.bytes)} ${r.kind === "in" ? "from" : "to"} chip ${peer}`).join(", ")}` : `\nnothing ${r.kind === "in" ? "arriving" : "leaving"} at ${fmtPs(t)}`;
      return `<b>chip ${chip} ${r.kind === "in" ? "incoming" : "outgoing"} cross-chip DMAs</b>  ${list.length}, ${bytes(total)}\npeers: ${peers.map((p) => "chip " + p).join(", ") || "none"} (colour = peer)${now}`;
    }
    const evs = m.hostByChip.get(chip) || [];
    return `<b>chip ${chip} host</b>  ${evs.filter((h) => h.ev === "fw_cmd").length} firmware commands, ${evs.filter((h) => h.ev === "irq").length} interrupts\nplaced on the virtual axis by anchors; see "about"`;
  }

  render() {
    for (const rec of this.cards.values()) this.drawCard(rec);
    this.drawBrushBar();
    this.drawMatrix();
    this.drawLegend();
  }

  drawCard(rec) {
    const m = this.model, W = this.opts.stripPx, dpr = window.devicePixelRatio || 1;
    const g = rec.cv.getContext("2d");
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, W, rec.height);
    const [s0] = this.span;
    const psPerPx = (this.span[1] - this.span[0]) / W;
    const out = this.tmp;
    const X = (t) => (t - s0) / psPerPx;
    for (const r of rec.rows) {
      g.fillStyle = unitRGB("idle");
      g.fillRect(0, r.y, W, r.h);
      if (r.kind === "tc" || (r.kind === "sc" && r.core)) {
        if (!r.core) {
          g.strokeStyle = unitRGB("line"); g.setLineDash([3, 3]); g.strokeRect(0.5, r.y + 0.5, W - 1, r.h - 1); g.setLineDash([]);
          continue;
        }
        // A SparseCore's tiles take the lower half of its strip.
        let h = r.h, tileTop = null;
        if (r.kind === "sc" && r.tiles) { h = Math.ceil(r.h / 2); tileTop = r.y + h; }
        const lvl = levelFor(this.bins.core(r.core), psPerPx);
        for (let x = 0; x < W; x++) {
          sampleColumn(lvl, s0 + x * psPerPx, s0 + (x + 1) * psPerPx, out);
          let yb = r.y + h;
          // Any issue at all leaves a mark: at a slice's scale a core's whole program
          // can be a fraction of one pixel, and a blank strip would say it did nothing.
          const bh = out.busy > 0 ? Math.max(2, Math.round(out.busy * h)) : 0, wh = Math.round(out.wait * h), nh = Math.round(out.nop * h);
          const hh = out.hwait > 0.02 ? Math.max(1, Math.round(Math.min(1, out.hwait) * h * 0.25)) : 0;
          if (bh) { g.fillStyle = out.dom === DOM_NONE ? unitRGB("dim") : unitRGB(DOMINANT[out.dom]); g.fillRect(x, yb - bh, 1, bh); yb -= bh; }
          if (wh) { g.fillStyle = unitRGB("wait"); g.fillRect(x, yb - wh, 1, wh); yb -= wh; }
          if (nh) { g.fillStyle = unitRGB("nop"); g.fillRect(x, yb - nh, 1, nh); yb -= nh; }
          if (!bh && !wh && !nh && hh) { g.fillStyle = unitRGB("host"); g.globalAlpha = 0.45; g.fillRect(x, r.y + h - hh, 1, hh); g.globalAlpha = 1; }
        }
        if (tileTop != null) {
          const tl = levelFor(this.bins.tiles(rec.chip, r.ci), psPerPx), th = r.y + r.h - tileTop;
          const n = this.bins.tiles(rec.chip, r.ci).nTiles;
          g.fillStyle = unitRGB("panel"); g.fillRect(0, tileTop, W, th);
          for (let x = 0; x < W; x++) {
            sampleColumn(tl, s0 + x * psPerPx, s0 + (x + 1) * psPerPx, out);
            if (!out.active) continue;
            const hh = Math.max(1, Math.round((th - 1) * Math.max(out.busy, 0.15 + out.wait * 0.5)));
            g.fillStyle = out.wait > out.busy ? unitRGB("wait") : unitRGB("sc");
            g.globalAlpha = 0.35 + 0.65 * out.active / n;
            g.fillRect(x, tileTop + th - hh, 1, hh);
          }
          g.globalAlpha = 1;
        }
      } else if (r.kind === "sc") {
        if (!r.seen) { g.strokeStyle = unitRGB("line"); g.setLineDash([2, 3]); g.strokeRect(0.5, r.y + 0.5, W - 1, r.h - 1); g.setLineDash([]); continue; }
        for (const i of this.links.dmaByIssuer.get(r.id) || []) {
          const d = m.dmas[i];
          const a = Number.isNaN(d.at) ? d.start : d.at, b = d.open ? a : d.end;
          g.fillStyle = unitRGB("dma"); g.fillRect(X(a), r.y, Math.max(1, (b - a) / psPerPx), r.h);
        }
        for (const i of this.links.waitsByRow.get(r.id) || []) {
          const w = m.waits[i];
          const b = w.open ? m.tEnd : w.t1;
          g.fillStyle = unitRGB("wait"); g.fillRect(X(w.t0), r.y, Math.max(1, (b - w.t0) / psPerPx), r.h);
        }
        for (const d of m.scDispatches) {
          if (d.chip !== rec.chip || d.ci !== r.ci) continue;
          g.fillStyle = unitRGB("sc"); g.fillRect(Math.round(X(d.ps)), r.y, 1, r.h);
        }
      } else if (r.kind === "in" || r.kind === "out") {
        for (const { d, peer } of this.iciDmas(rec.chip, r.kind)) {
          const a = Number.isNaN(d.at) ? d.start : d.at, b = d.open ? m.tEnd : d.end;
          if (Number.isNaN(a)) continue;
          g.fillStyle = peerColor(peer);
          g.fillRect(X(a), r.y, Math.max(1.5, (b - a) / psPerPx), r.h);
        }
      } else {
        for (const h of m.hostByChip.get(rec.chip) || []) {
          if (h.ev !== "fw_cmd") continue;
          const [a, b] = h.span;
          g.fillStyle = h.kind === "dma" ? unitRGB("dma") : h.kind === "run" ? unitRGB("accent") : unitRGB("host");
          g.globalAlpha = h.placed === 0 ? 1 : 0.5;
          g.fillRect(X(a), r.y, Math.max(1, (b - a) / psPerPx), r.h);
        }
        g.globalAlpha = 1;
      }
    }
    // The chip's own end, and the brush window.
    const te = m.chipTend.get(rec.chip);
    if (te != null && te < this.span[1]) { g.strokeStyle = unitRGB("dimmer"); g.setLineDash([2, 2]); const x = Math.round(X(te)) + 0.5; g.beginPath(); g.moveTo(x, 0); g.lineTo(x, rec.height); g.stroke(); g.setLineDash([]); }
    const [b0, b1] = this.brush;
    if (b0 > this.span[0] || b1 < this.span[1]) {
      g.fillStyle = unitRGB("bg"); g.globalAlpha = 0.55;
      g.fillRect(0, 0, Math.max(0, X(b0)), rec.height);
      g.fillRect(Math.min(W, X(b1)), 0, W - Math.min(W, X(b1)), rec.height);
      g.globalAlpha = 1;
      g.strokeStyle = unitRGB("sel");
      g.strokeRect(Math.round(X(b0)) + 0.5, 0.5, Math.max(1, (b1 - b0) / psPerPx), rec.height - 1);
    }
    rec.card.classList.toggle("hl", this.hl != null && rec.rows.some((r) => r.id === this.hl));
  }

  // --- brush -----------------------------------------------------------------------

  buildBrush() {
    const m = this.model;
    const host = this.opts.brushRoot || this.root;
    const wrap = el("div", "sm-brush");
    wrap.title = "the whole run in one strip: blue = a chip issuing, red = cores waiting; the lit part is the window the timeline shows";
    const bar = el("canvas", "sm-brush-bar");
    this.brushW = 0;
    const lo = el("div", "sm-handle lo"), hi = el("div", "sm-handle hi"), win = el("div", "sm-window");
    wrap.appendChild(bar); wrap.appendChild(win); wrap.appendChild(lo); wrap.appendChild(hi);
    const info = el("div", "sm-brush-info dim");
    host.appendChild(wrap);
    host.appendChild(info);
    this.brushEls = { wrap, bar, lo, hi, win, info };
    bindWindow(wrap, { width: () => wrap.clientWidth, span: () => this.span, get: () => this.brush, set: (a, b) => this.setBrush(a, b, true) });
    wrap.addEventListener("dblclick", () => this.setBrush(this.span[0], this.span[1], true));
  }

  setBrush(t0, t1, emit) {
    const [S0, S1] = this.span;
    t0 = clamp(t0, S0, S1); t1 = clamp(t1, S0, S1);
    if (t1 <= t0) t1 = Math.min(S1, t0 + 1);
    if (t0 === this.brush[0] && t1 === this.brush[1]) return;
    this.brush = [t0, t1];
    for (const rec of this.cards.values()) this.drawCard(rec);
    this.drawBrushBar();
    this.drawMatrix();
    if (emit && this.opts.onBrush) this.opts.onBrush(t0, t1);
  }

  drawBrushBar() {
    if (!this.brushEls) return;
    const m = this.model, { wrap, bar, lo, hi, win, info } = this.brushEls;
    const W = wrap.clientWidth || 600, dpr = window.devicePixelRatio || 1;
    if (bar.width !== Math.round(W * dpr)) { bar.width = Math.round(W * dpr); bar.height = Math.round(18 * dpr); bar.style.width = W + "px"; bar.style.height = "18px"; }
    const g = bar.getContext("2d");
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, W, 18);
    const [S0, S1] = this.span;
    const psPerPx = (S1 - S0) / W, out = this.tmp;
    for (const chip of m.chipIds) {
      const lvl = levelFor(this.bins.chip(chip), psPerPx);
      for (let x = 0; x < W; x++) {
        sampleColumn(lvl, S0 + x * psPerPx, S0 + (x + 1) * psPerPx, out);
        if (out.busy > 0) { const bh = Math.max(2, out.busy * 16); g.fillStyle = unitRGB("accent"); g.globalAlpha = Math.max(0.4, Math.min(1, out.busy)); g.fillRect(x, 18 - bh, 1, bh); }
        if (out.wait > 0.02) { g.fillStyle = unitRGB("wait"); g.globalAlpha = Math.min(1, out.wait); g.fillRect(x, 0, 1, out.wait * 8); }
      }
    }
    g.globalAlpha = 1;
    const [b0, b1] = this.brush;
    const pct = (t) => ((t - S0) / (S1 - S0) * 100) + "%";
    lo.style.left = pct(b0);
    hi.style.left = pct(b1);
    win.style.left = pct(b0);
    win.style.width = ((b1 - b0) / (S1 - S0) * 100) + "%";
    info.textContent = `window ${fmtPs(b0)} – ${fmtPs(b1)} (${fmtPs(b1 - b0)})  ·  the strips span the cores' activity, ${fmtPs(S0)} – ${fmtPs(S1)} of a ${fmtPs(m.tEnd)} run  ·  drag to select, drag the edges or the inside to adjust, wheel to zoom, double-click to reset`;
  }

  crossDmas() {
    const [b0, b1] = this.brush, m = this.model;
    return m.dmas.filter((d) => d.cross && !Number.isNaN(d.at) && d.at <= b1 && (d.open ? m.tEnd : d.end) >= b0);
  }

  // --- cross-chip traffic ----------------------------------------------------------

  // The cross-chip DMAs touching `chip`: [{d, peer}], "in" = landing here, "out" = leaving.
  iciDmas(chip, dir) {
    const key = chip + ":" + dir;
    if (!this._ici) this._ici = new Map();
    let out = this._ici.get(key);
    if (out) return out;
    out = [];
    for (const d of this.model.dmas) {
      if (!d.cross) continue;
      if (dir === "in" && d.dstChips.has(chip)) for (const p of d.srcChips) if (p !== chip) out.push({ d, peer: p });
      if (dir === "out" && d.srcChips.has(chip)) for (const p of d.dstChips) if (p !== chip) out.push({ d, peer: p });
    }
    out.sort((x, y) => (x.d.at || 0) - (y.d.at || 0));
    this._ici.set(key, out);
    return out;
  }

  // Source x destination inside the window: {n, bytes, dmas} per pair, plus totals.
  crossMatrix() {
    const [b0, b1] = this.brush, m = this.model;
    const cells = new Map();
    let n = 0, total = 0;
    for (const d of m.dmas) {
      if (!d.cross) continue;
      const a = Number.isNaN(d.at) ? d.start : d.at, e = d.open ? m.tEnd : d.end;
      if (Number.isNaN(a) || a > b1 || e < b0) continue;
      n++; total += d.bytes || 0;
      for (const sc of d.srcChips) for (const dc of d.dstChips) {
        if (sc === dc) continue;
        const k = sc + ">" + dc;
        let c = cells.get(k);
        if (!c) { c = { src: sc, dst: dc, n: 0, bytes: 0, dmas: [] }; cells.set(k, c); }
        c.n++; c.bytes += d.bytes || 0; c.dmas.push(d);
      }
    }
    return { cells, n, total };
  }

  drawMatrix() {
    const m = this.model, box = this.matrix;
    box.textContent = "";
    if (m.chipIds.length < 2) return;
    const { cells, n, total } = this.crossMatrix();
    const allCross = m.dmas.filter((d) => d.cross).length;
    box.appendChild(el("div", "sm-matrix-title", `cross-chip DMAs in the window: ${n} of ${allCross}, ${bytes(total)}`));
    const chips = m.chipIds;
    const t = el("table", "sm-mx");
    const head = el("tr");
    head.appendChild(el("th", "corner", "from \\ to"));
    for (const c of chips) { const th = el("th", null, String(c)); th.style.color = peerColor(c); th.title = m.chipLabel(c); head.appendChild(th); }
    t.appendChild(head);
    let max = 0;
    for (const c of cells.values()) max = Math.max(max, c.bytes);
    for (const src of chips) {
      const tr = el("tr");
      const th = el("th", null, String(src)); th.style.color = peerColor(src); th.title = m.chipLabel(src);
      tr.appendChild(th);
      for (const dst of chips) {
        const td = el("td", src === dst ? "self" : "");
        if (src !== dst) {
          const c = cells.get(src + ">" + dst);
          if (c) {
            const f = max ? 0.12 + 0.45 * (c.bytes / max) : 0.3;
            td.style.background = `color-mix(in srgb, var(--accent) ${Math.round(f * 100)}%, transparent)`;
            td.innerHTML = `<span class="n">${c.n}</span><span class="b">${esc(bytes(c.bytes))}</span>`;
            td.title = `chip ${src} → chip ${dst}: ${c.n} DMAs, ${bytes(c.bytes)} in the window\nby ${[...new Set(c.dmas.map((d) => issuer(d.by)))].join(", ")}\nclick to select the first`;
            td.classList.add("click");
            td.onclick = () => { if (this.opts.onCellClick) this.opts.onCellClick(c); };
            td.onpointerenter = () => this.highlightPair(src, dst);
            td.onpointerleave = () => this.highlightPair(null, null);
          } else td.textContent = "·";
        }
        tr.appendChild(td);
      }
      t.appendChild(tr);
    }
    box.appendChild(t);
  }

  highlightPair(src, dst) {
    for (const rec of this.cards.values()) rec.card.classList.toggle("hl", src != null && (rec.chip === src || rec.chip === dst));
  }

  // The units this run issued, from the coarsest bins of each chip: their masks are
  // the OR of every stream's, so the key names this run's units and not the ISA's.
  unitsSeen() {
    if (this._units) return this._units;
    let mask = 0;
    for (const chip of this.model.chipIds) {
      const lvl = levelFor(this.bins.chip(chip), Infinity);
      for (let i = 0; i < lvl.n; i++) mask |= lvl.mask[i];
    }
    this._units = UNIT_ABOUT.filter(([u]) => mask & UNIT_BIT[u]);
    return this._units;
  }

  // A swatch per unit, so "colour = unit" is a key and not a promise. It stays on one
  // line: what does not fit waits behind a "+n".
  unitKey() {
    const box = el("span", "sm-units");
    const seen = this.unitsSeen();
    box.appendChild(el("span", "dim", seen.length ? "issuing (colour = unit):" : "issuing (colour = unit)"));
    if (!seen.length) return box;
    const us = seen.map(([u, what]) => {
      const s = el("span", "u");
      const sw = el("span", "swatch");
      sw.style.background = unitColor(u);
      s.appendChild(sw);
      s.appendChild(document.createTextNode(u));
      s.title = what;
      box.appendChild(s);
      return s;
    });
    const more = el("button", "more");
    more.title = "the rest of the units this run issued";
    box.appendChild(more);
    requestAnimationFrame(() => this.fitUnits(box, us, more));
    return box;
  }

  fitUnits(box, us, more) {
    if (!box.isConnected) return;
    let n = us.length;
    const apply = () => {
      us.forEach((s, i) => { s.hidden = i >= n; });
      more.hidden = n >= us.length;
      more.textContent = `+${us.length - n}`;
    };
    apply();
    // One row of the legend is about 16px; anything taller has wrapped.
    while (n > 2 && box.getBoundingClientRect().height > 22) { n--; apply(); }
    more.onclick = (e) => { e.stopPropagation(); for (const s of us) s.hidden = false; more.hidden = true; };
  }

  drawLegend() {
    const items = [["waiting on another core", "var(--wait)"], ["idle for the host", "var(--host)"], ["empty bundles", "var(--nop)"], ["DMA transfer", "var(--u-dma)"], ["tiles active", "var(--sc)"]];
    this.legend.textContent = "";
    this.legend.appendChild(this.unitKey());
    for (const [t, c] of items) {
      const s = el("span");
      const sw = el("span", "swatch"); sw.style.background = c;
      s.appendChild(sw); s.appendChild(document.createTextNode(t));
      this.legend.appendChild(s);
    }
    if (this.model.chipIds.length > 1) this.legend.appendChild(el("span", "dim", "in / out strips: cross-chip DMAs, colour = the peer chip"));
  }

  highlightRow(id) {
    this.hl = id;
    for (const rec of this.cards.values()) rec.card.classList.toggle("hl", id != null && rec.rows.some((r) => r.id === id));
  }
}
